import React from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { SignalIcon } from "@heroicons/react/24/outline";

/**
 * Component for displaying a single integration card
 * Extracted from the Integrations page for better organization and reusability
 */
const IntegrationCard = ({ integration, t, onButtonClick, onDisconnect }) => {
  const { key, title, status, tags, id } = integration;
  const navigate = useNavigate();

  // Style based on integration status
  const getStatusClass = () => {
    switch (status) {
      case "connected":
        return "bg-green-100 text-green-800";
      case "available":
        return "bg-indigo-100 text-indigo-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Button style based on integration status
  const getButtonClass = () => {
    switch (status) {
      case "connected":
        return "bg-gray-600 text-white hover:bg-gray-500";
      case "available":
        return "bg-indigo-600 text-white hover:bg-indigo-500";
      default:
        return "bg-gray-100 text-gray-400 cursor-not-allowed";
    }
  };

  // Button text based on integration status
  const getButtonText = () => {
    switch (status) {
      case "connected":
        return t("integrations.configure");
      case "available":
        return t("integrations.connect");
      default:
        return t("integrations.comingSoon");
    }
  };

  // Handle the button click based on status
  const handleButtonClick = () => {
    if (status === "connected" && id) {
      // Navigate to the configuration page for connected integrations
      navigate(`/integration/${id}/configure`);
    } else {
      // Use the provided onClick handler for other cases
      onButtonClick();
    }
  };

  return (
    <div className="grid content-between bg-white p-6 rounded-lg shadow-xs hover:shadow-md transition-shadow duration-300">
      <div>
        <div className="flex items-center justify-between mb-4">
          {key === "webhook" ? (
            <SignalIcon className="h-8 w-8 text-indigo-600" />
          ) : (
            <img
              src={`https://media.loyaltify.io/static/integrations/${key}.png`}
              alt={title}
              className="h-8"
            />
          )}
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusClass()}`}
          >
            {status === "connected"
              ? t("integrations.connected")
              : status === "available"
                ? t("integrations.available")
                : t("integrations.comingSoon")}
          </span>
        </div>
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>

        {/* Tags display */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2 mb-1">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <p className="mt-2 text-sm text-gray-500">
          {t(`integrations.description.${key}`)}
        </p>
      </div>

      {status === "connected" ? (
        <div className="flex gap-2 mt-4">
          <button
            onClick={handleButtonClick}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold shadow-xs ${getButtonClass()}`}
            aria-label={`${getButtonText()} ${title}`}
          >
            {getButtonText()}
          </button>
          <button
            onClick={onDisconnect}
            className="flex-1 rounded-md px-3 py-2 text-sm font-semibold shadow-xs bg-red-600 text-white hover:bg-red-500"
            aria-label={`Disconnect ${title}`}
          >
            {t("integrations.disconnect")}
          </button>
        </div>
      ) : (
        <button
          onClick={handleButtonClick}
          disabled={status === "coming_soon"}
          className={`mt-4 w-full rounded-md px-3 py-2 text-sm font-semibold shadow-xs ${getButtonClass()}`}
          aria-label={`${getButtonText()} ${title}`}
        >
          {getButtonText()}
        </button>
      )}
    </div>
  );
};

IntegrationCard.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    tags: PropTypes.array,
  }).isRequired,
  t: PropTypes.func.isRequired,
  onButtonClick: PropTypes.func.isRequired,
  onDisconnect: PropTypes.func,
};

export default React.memo(IntegrationCard);
